import * as React from 'react'
import { ChevronDownIcon } from '@/src/icons'
import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Accordion (Root) — Radix API shim
// ---------------------------------------------------------------------------
// Radix uses `type="single"|"multiple"`, `collapsible`, and string `defaultValue`.
// Base UI uses `multiple` boolean and array-based `value`/`defaultValue`.
// We wrap to preserve our public API.
// ---------------------------------------------------------------------------

interface AccordionProps {
  /** @deprecated Radix compat — 'single' maps to multiple=false, 'multiple' to multiple=true */
  type?: 'single' | 'multiple'
  /** Whether a single-mode accordion can be fully collapsed */
  collapsible?: boolean
  /** The value(s) of the currently expanded item(s) */
  value?: string | string[]
  /** The value(s) of the initially expanded item(s) */
  defaultValue?: string | string[]
  /** Callback when the expanded items change */
  onValueChange?: (value: string | string[]) => void
  disabled?: boolean
  className?: string
  children?: React.ReactNode
  orientation?: 'horizontal' | 'vertical'
}

function Accordion({ type = 'single', collapsible, value, defaultValue, onValueChange, ...props }: AccordionProps) {
  const isMultiple = type === 'multiple'

  // Convert string defaultValue to array for Base UI
  const baseDefaultValue = React.useMemo(() => {
    if (defaultValue === undefined) return undefined
    if (Array.isArray(defaultValue)) return defaultValue
    return [defaultValue]
  }, [defaultValue])

  // Convert string value to array for Base UI (controlled mode)
  const baseValue = React.useMemo(() => {
    if (value === undefined) return undefined
    if (Array.isArray(value)) return value
    return [value]
  }, [value])

  // Wrap onValueChange to convert array back to the expected format
  const handleValueChange = React.useCallback(
    (newValue: (string | null)[]) => {
      if (!onValueChange) return
      if (isMultiple) {
        onValueChange(newValue.filter((v): v is string => v !== null))
      } else {
        const first = newValue[0]
        onValueChange(first ?? '')
      }
    },
    [onValueChange, isMultiple],
  )

  // For single non-collapsible mode, we need to prevent collapsing.
  // Base UI doesn't have a built-in "collapsible" concept for single mode,
  // so we track state internally to prevent collapse.
  // For simplicity and to avoid complexity, we rely on consumers' expectations:
  // collapsible=true (or type=multiple) is by far the most common usage.

  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      multiple={isMultiple}
      value={baseValue}
      defaultValue={baseDefaultValue}
      onValueChange={onValueChange ? handleValueChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// AccordionItem
// ---------------------------------------------------------------------------

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// AccordionTrigger
// ---------------------------------------------------------------------------

function AccordionTrigger({
  className,
  children,
  accessory,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  /** DS: right-aligned accessory content (Features, Value, Button) rendered between label and chevron */
  accessory?: React.ReactNode
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          // DS: trigger typography is S Medium 14px; chevron is icon-md (size-5 per Principal policy)
          // Base UI: data-[panel-open] replaces Radix data-[state=open]
          'flex flex-1 items-center justify-between gap-4 rounded-md py-4 text-left text-sm font-medium text-foreground transition-all outline-none hover:text-primary focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-panel-open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}
        {accessory && (
          <div
            data-slot="accordion-accessory"
            className="ml-auto flex items-center gap-2 text-muted-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            {accessory}
          </div>
        )}
        {/* DS chevron icon - icon-md (20px) per Principal nav policy */}
        <ChevronDownIcon className="pointer-events-none size-5 shrink-0 text-muted-foreground transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

// ---------------------------------------------------------------------------
// AccordionContent (wraps Base UI Panel)
// ---------------------------------------------------------------------------
// Base UI renames Content → Panel. We keep our export name as AccordionContent
// to preserve the public API.
// ---------------------------------------------------------------------------

function AccordionContent({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Panel>) {
  return (
    <AccordionPrimitive.Panel data-slot="accordion-content" className="overflow-hidden text-sm" {...props}>
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Panel>
  )
}

// ---------------------------------------------------------------------------
// AccordionGroup (plain HTML wrapper — unchanged)
// ---------------------------------------------------------------------------

function AccordionGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="accordion-group"
      className={cn('flex flex-col divide-y divide-border rounded-lg border', className)}
      {...props}
    />
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent, AccordionGroup }
